// libs
import React, { memo, useCallback } from 'react';
import escapeRegExp from 'lodash/escapeRegExp';
import isEmpty from 'lodash/isEmpty';

// hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// constants
import { SEARCH_TYPE, SUGGESTION_TYPES } from 'shared/components/GlobalSearch/constants';

// helpers
import {
  generateTrackingURLForSearch,
  trackSearchCall,
} from 'shared/components/GlobalSearch/helpers';
import { addToRecentSearch } from 'shared/components/GlobalSearch/helpers';
import { getPlatformType } from 'shared/components/AppRedirectionPopup/components/AppRedirectionPopup';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import sanitizeHTML from 'shared/helpers/utils/sanitizeHTML';
import {
  resolveUrlFromCatalogTag,
  getCatalogTagStoreId,
  getStoreUrl,
} from 'shared/helpers/storeUrl';

// components
import ElementWrapper from 'shared/components/GlobalSearch/components/KeyboardWrapper/ElementWrapper';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useStoreId } from 'shared/store/app/hooks';

// context
import { useKeyboardEventContext } from 'shared/components/GlobalSearch/components/KeyboardWrapper/KeyboardContext';
import { useRedirectionContext } from 'shared/components/AppRedirectionPopup/Provider/context';

// styles
import {
  Term,
  SuggestionContainerMobile,
  SuggestionContainerDesktop,
  Icon,
  Image,
  DesktopTerm,
} from './styles';

// defs
import { ICON_TYPE, ISuggestion } from 'store/globalSearch/types';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

const ICON_SIZE = '20px';

interface ISuggestionProps {
  term: string;
  icon: ICON_TYPE;
  isRipple: boolean;
  indexValue: number;
  maxRecentSearches: number;
  suggestion: ISuggestion;
  onCategorySwitch: (storeToRedirect: string, redirectUrl: string, productName: string) => void;
}
const getIcon = (
  icon: string,
  theme: Record<string, Record<string, string>>,
  SearchRecent: any,
  Trending: any,
  Search: any
) => {
  if (icon === ICON_TYPE.RECENT)
    return <SearchRecent size={ICON_SIZE} color={theme?.colors?.suggestionIconColor} />;
  if (icon === ICON_TYPE.TRENDING)
    return <Trending size={ICON_SIZE} color={theme?.colors?.suggestionIconColor} />;
  if (icon === ICON_TYPE.SEARCH)
    return <Search size={ICON_SIZE} color={theme?.colors?.suggestionIconColor} />;
};
function Suggestion({
  term,
  icon,
  isRipple,
  indexValue,
  maxRecentSearches,
  suggestion,
  onCategorySwitch,
}: ISuggestionProps) {
  const { currentIndex } = useKeyboardEventContext();
  const { showRedirectionPopup } = useRedirectionContext();
  const theme: Theme = useTheme();
  const SearchRecent = useStoreIcon(IconName.SEARCH_RECENT);
  const Trending = useStoreIcon(IconName.TREND_LINE);
  const Search = useStoreIcon(IconName.SEARCH);
  // @ts-ignore: any since store can't be hardcoded as enums
  const storeSwitchBanner = useRemoteConfig('storeSwitchBanner');
  const currentStoreId = useStoreId();

  const {
    suggestionWord,
    suggestionSubtitle,
    subtext,
    type,
    image_url: imageUrl,
    id,
    catalog_tag,
  } = suggestion;
  const url = resolveUrlFromCatalogTag(suggestion);

  const isActive = currentIndex === indexValue;

  const goToUrl = useCallback(() => {
    const isCrossPlatformRedirect = type === 'query';

    const redirectionURL = isCrossPlatformRedirect ? id || url : url;

    addToRecentSearch(
      {
        url: redirectionURL,
        suggestionWord,
        suggestionSubtitle,
        type,
        catalog_tag,
      },
      maxRecentSearches
    );
    trackSearchCall({
      typedSearchTerm: suggestionSubtitle || '',
      searchTerm: suggestionWord,
      searchType: SEARCH_TYPE.AUTO_COMPLETE,
      clickPosition: indexValue,
      clickMetadata: {
        page_search_term: term,
        position: indexValue,
        q: suggestionWord,
        url: url,
        type: type,
        id: id,
      },
    });
    if (isCrossPlatformRedirect && !isEmpty(id)) {
      if (isRipple) {
        const platform = getPlatformType(id);

        const redirectToPlatform = () => (window.location.href = id);
        showRedirectionPopup({ onConfirm: redirectToPlatform, platform });
        return;
      }
      window.open(id, '_blank');
      return;
    }

    if (type === SUGGESTION_TYPES.CATEGORY || type === SUGGESTION_TYPES.BRAND) {
      const crossStoreId = getCatalogTagStoreId(catalog_tag, currentStoreId);
      if (crossStoreId) {
        // @ts-ignore: any since store can't be hardcoded as enums
        const storeConfig = storeSwitchBanner?.[crossStoreId];
        const storeSwitch =
          (type === SUGGESTION_TYPES.BRAND && storeConfig?.brandStoreSwitch?.enabled) ||
          (type === SUGGESTION_TYPES.CATEGORY && storeConfig?.categoryStoreSwitch?.enabled);
        if (storeSwitch) {
          onCategorySwitch(crossStoreId, url, suggestionWord);
          return;
        }

        NFA.updateDatalayerBeforeNext({ suggestionPosition: indexValue + 1 });
        window.location.href = getStoreUrl(url);
        return;
      }
    }

    NFA.updateDatalayerBeforeNext({ suggestionPosition: indexValue + 1 });

    window.location.href = generateTrackingURLForSearch({
      url: redirectionURL,
      searchType: SEARCH_TYPE.AUTO_COMPLETE,
      internalSearchTerm: suggestionWord,
      typedSearchTerm: term,
      suggestionType: type,
      suggestionSubtitle,
    });
  }, [
    id,
    indexValue,
    isRipple,
    maxRecentSearches,
    onCategorySwitch,
    showRedirectionPopup,
    storeSwitchBanner,
    suggestionSubtitle,
    suggestionWord,
    term,
    type,
    url,
    catalog_tag,
    currentStoreId,
  ]);

  const regxp = new RegExp(`(${escapeRegExp(term)})`, 'ig');
  const decoratedTitle = suggestionWord.replace(regxp, "<span class='sg-hl'>$1</span>");

  const handleKeyDown = (e: React.KeyboardEvent, callback: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const suggestionText = `${suggestionWord} ${suggestionSubtitle || ''}`.trim();

  const commonProps = {
    className: 'sg-anc',
    'data-test': 'suggestion-anchor',
    onClick: goToUrl,
    'data-at': `suggestion-${indexValue}`,
    role: 'option',
    'aria-label': suggestionText,
    'aria-selected': isActive,
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, goToUrl),
  };
  const sanitizedDecoratedTitle = sanitizeHTML(decoratedTitle);
  let element = (
    <SuggestionContainerMobile href={url} preventDefault {...commonProps}>
      <Icon>{getIcon(icon, theme, SearchRecent, Trending, Search)}</Icon>
      <Term>
        <span
          data-test="suggestion-title"
          dangerouslySetInnerHTML={{
            __html: sanitizedDecoratedTitle,
          }}
          data-cs-encrypt="true"
        />
        <span className="sg-guide" data-test="suggestion-subtitle" data-cs-encrypt="true">
          {suggestionSubtitle}
        </span>
        {subtext && <div className="sg-sub-text">{subtext}</div>}
      </Term>
      {imageUrl && <Image src={imageUrl} />}
    </SuggestionContainerMobile>
  );

  if (!isRipple) {
    element = (
      <ElementWrapper onClick={goToUrl} indexValue={indexValue}>
        <SuggestionContainerDesktop {...commonProps} active={isActive}>
          <Icon>{getIcon(icon, theme, SearchRecent, Trending, Search)}</Icon>

          <DesktopTerm>
            <span
              data-test="suggestion-title"
              dangerouslySetInnerHTML={{
                __html: sanitizedDecoratedTitle,
              }}
            />
            <span className="sg-guide" data-test="suggestion-subtitle">
              {suggestionSubtitle}
            </span>
            {subtext && <div className="sg-sub-text">{subtext}</div>}
          </DesktopTerm>
          {imageUrl && <Image src={imageUrl} />}
        </SuggestionContainerDesktop>
      </ElementWrapper>
    );
  }

  return <>{element}</>;
}

export default memo(Suggestion);
